import { isEmpty } from '@jjj/components';
import actions from './actions';
import { createReducer } from '../../../helpers/reducer';
import { keyBy } from '../../../helpers/core';
import { toggleMaStatusUrl } from '../../../helpers/maStatus';
import { t } from '../../../i18n';
import { getCountryConfigById, isISR, isPostalCode } from '../../../helpers/countriesHelpers';

const initState = {
  bookingLoading: false,
  isApprovedAfterJobClosedLoading: false,
  isWorkerSubmissionsLoading: false,
  buttonToShowLoader: null,
  countries: [],
  deleted: false,
  editForm: null,
  formInfo: { pauseReasonsComment: '', selectedValue: '' },
  isAddOtherPaymentsModalShown: false,
  isDeletionRequestPending: false,
  isModalShown: false,
  isShowAddGigModal: false,
  isShowJobModal: false,
  isShowAccreditationSummaryModal: false,
  isShowMore: false,
  job: null,
  loading: false,
  sprintActionInfo: null,
  isReopenPoolsLoading: false,
  isShowConsolidatedInvoiceLoading: false,
  consolidatedInvoice: [],
};

const initialJobData = {
  address: '',
  city: '',
  country: '',
  state: '',
  street: '',
  street2: '',
  zip: '',
};

function validate(attr, value, country) {
  let error = '';

  if (attr === 'street') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.jobs.street_label') });
    }
  }

  if (attr === 'city') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.jobs.city_label') });
    }
  }

  if (!isISR() && attr === 'state') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.jobs.state_label') });
    }
  }

  if (attr === 'country') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.jobs.country_label') });
    }
  }

  if (attr === 'zip') {
    if (isEmpty(value)) {
      if (!isISR()) {
        error = t('dash.validation.required_error', {
          fieldName: t('dash.common.location.postal_code'),
        });
      }
    } else if (country && !isPostalCode(value, country)) {
      error = t('dash.validation.valid_error', {
        fieldName: t('dash.common.location.postal_code'),
      });
    }
  }

  if (attr === 'lat' || attr === 'lng') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.jobs.address_label') });
    }
  }

  return error;
}

function onLoad(state) {
  return {
    ...state,
    loading: true,
    bookingLoading: true,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    loading: false,
    bookingLoading: false,
    ...data,
  };
}

function onLoadShiftSuccess(state, { payload }) {
  const { bookingId } = payload;

  let currentBooking;
  const { job } = state;

  if (job && job.bookings) {
    currentBooking = job.bookings.find(booking => booking.id === bookingId);

    if (currentBooking) {
      currentBooking.shifts = payload;
    }
  }

  return {
    ...state,
    job,
    loading: false,
  };
}

function onLoadApprovedAfterJobClosedRequest(state) {
  return {
    ...state,
    isApprovedAfterJobClosedLoading: true,
  };
}

function onLoadApprovedAfterJobClosedSuccess(state, { payload }) {
  const { bookingId } = payload;

  let currentBooking;
  const { job } = state;

  if (job && job.bookings) {
    currentBooking = job.bookings.find(booking => booking.id === bookingId);

    if (currentBooking) {
      currentBooking.approved_after_job_closed_shifts = payload;
    }
  }

  return {
    ...state,
    isApprovedAfterJobClosedLoading: false,
    job,
  };
}

function onLoadWorkerSubmissionsRequest(state) {
  return {
    ...state,
    isWorkerSubmissionsLoading: true,
  };
}

function onLoadWorkerSubmissionsSuccess(state, { payload }) {
  const { bookingId } = payload;

  let currentBooking;
  const { job } = state;

  if (job && job.bookings) {
    currentBooking = job.bookings.find(booking => booking.id === bookingId);

    if (currentBooking) {
      currentBooking.added_after_job_closed_shifts = payload;
    }
  }

  return {
    ...state,
    isWorkerSubmissionsLoading: false,
    job,
  };
}

function onLoadCancelledShiftSuccess(state, { payload }) {
  const { bookingId } = payload;
  let currentBooking;
  const { job } = state;

  if (job && job.bookings) {
    currentBooking = job.bookings.find(booking => booking.id === bookingId);

    if (currentBooking) {
      currentBooking.backup_shifts = payload;
    }
  }

  return {
    ...state,
    job,
    loading: false,
  };
}

function onLoadBackupShiftSuccess(state, { payload }) {
  const { bookingId } = payload;
  let currentBooking;
  const { job } = state;

  if (job && job.bookings) {
    currentBooking = job.bookings.find(booking => booking.id === bookingId);

    if (currentBooking) {
      currentBooking.backup_shifts = payload;
    }
  }

  return {
    ...state,
    job,
    loading: false,
  };
}

function onLoadReportedShiftSuccess(state, { payload }) {
  const { bookingId } = payload;
  let currentBooking;
  const { job } = state;

  if (job && job.bookings) {
    currentBooking = job.bookings.find(booking => booking.id === bookingId);

    if (currentBooking) {
      currentBooking.reported_shifts = payload;
    }
  }

  return {
    ...state,
    job,
    loading: false,
  };
}

function onDeleteRequest(state) {
  return {
    ...state,
    isDeletionRequestPending: true,
  };
}

function onDeleteSuccess(state) {
  return {
    ...state,
    deleted: true,
  };
}

function onEdit(state, { payload }) {
  const { address, country } = payload;
  const editObject = {
    ...initialJobData,
    ...address,
    country,
  };
  const propsArray = Object.keys(editObject);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: editObject[prop],
      error: validate(prop, editObject[prop]),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    editForm,
  };
}

function onSaveSuccess(state, { payload }) {
  const { data } = payload;
  const job = data;

  return {
    ...state,
    job: {
      ...state.job,
      ...job,
    },
    isEdit: false,
    editForm: null,
  };
}

function onSave(state, { payload }) {
  const { notify_workers } = payload;
  const buttonToShowLoader = notify_workers ? 'notify' : 'no-notify';

  return {
    ...state,
    buttonToShowLoader,
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm, countries } = state;

  const country = getCountryConfigById(editForm?.country?.value, countries);

  editForm[attr] = {
    value,
    error: validate(attr, value, country?.alpha_2_code),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop]),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onClearStore() {
  return { ...initState };
}

function onGetLocation(state, { payload }) {
  return {
    ...state,
    job: {
      ...state.job,
      location: payload,
    },
    isEdit: false,
    editForm: null,
  };
}

function onShowAddOtherPaymentsModal(state) {
  const { isAddOtherPaymentsModalShown } = state;

  return {
    ...state,
    isAddOtherPaymentsModalShown: !isAddOtherPaymentsModalShown,
  };
}

function onToggleShowMore(state) {
  const { isShowMore } = state;

  return {
    ...state,
    isShowMore: !isShowMore,
  };
}

function onShowModal(state, { payload }) {
  const { starts_at, action } = payload;

  return {
    ...state,
    isModalShown: true,
    sprintActionInfo: {
      ...action,
      starts_at,
    },
  };
}

function onShowJobModal(state) {
  return {
    ...state,
    isShowJobModal: true,
  };
}

function onShowAddGigModal(state) {
  const { isShowAddGigModal } = state;

  return {
    ...state,
    isShowAddGigModal: !isShowAddGigModal,
  };
}

function onPauseReasons(state, payload) {
  const {
    payload: { pause_action_reasons, resume_action_reasons },
  } = payload;

  return {
    ...state,
    formInfo: {
      pauseReasons: pause_action_reasons || resume_action_reasons,
    },
  };
}

function onChangeReasonComment(state, payload) {
  const { formInfo } = state;

  return {
    ...state,
    formInfo: {
      ...formInfo,
      pauseReasonsComment: payload.payload,
    },
  };
}

function onchangeReason(state, payload) {
  const { formInfo } = state;

  return {
    ...state,
    formInfo: {
      ...formInfo,
      selectedValue: payload.payload,
    },
  };
}

function onMaCall(state) {
  return {
    ...state,
    isMaLoading: true,
  };
}

function resumeMaSuccess(state) {
  return {
    ...state,
    isMaLoading: true,
  };
}

function updatePauseMaAction(state) {
  const { job } = state;

  return {
    ...state,
    job: {
      ...job,
      ma_state: 'paused',
      actions: job.actions.map(action => {
        const { url } = action;
        const pauseUrl = toggleMaStatusUrl(url);

        return action.key === 'pause_ma'
          ? { ...action, key: 'resume_ma', name: t('dash.jobs.job_actions.resume_ma'), url: pauseUrl }
          : action;
      }),
    },
  };
}

function updateResumeMaAction(state) {
  const { job } = state;

  return {
    ...state,
    job: {
      ...job,
      ma_state: 'running',
      actions: job.actions.map(action => {
        const { url } = action;
        const resumeUrl = toggleMaStatusUrl(url);

        return action.key === 'resume_ma'
          ? { ...action, key: 'pause_ma', name: t('dash.jobs.job_actions.pause_ma'), url: resumeUrl }
          : action;
      }),
    },
  };
}

function onCloseModal(state) {
  const { formInfo } = state;

  return {
    ...state,
    isMaLoading: false,
    isModalShown: false,
    isShowJobModal: false,
    formInfo: {
      ...formInfo,
      selectedValue: '',
      pauseReasonsComment: '',
    },
  };
}

function showAccreditationSummaryModal(state) {
  return { ...state, isShowAccreditationSummaryModal: true };
}

function hideAccreditationSummaryModal(state) {
  return { ...state, isShowAccreditationSummaryModal: false };
}

function reopenPools(state) {
  return {
    ...state,
    isReopenPoolsLoading: true,
  };
}

function reopenPoolsSuccess(state) {
  return {
    ...state,
    isReopenPoolsLoading: false,
  };
}

function reopenPoolsError(state) {
  return {
    ...state,
    isReopenPoolsLoading: false,
  };
}

function onShowConsolidatedInvoiceRequest(state) {
  return {
    ...state,
    isShowConsolidatedInvoiceLoading: true,
  };
}

function onShowConsolidatedInvoiceSuccess(state, { payload }) {
  return {
    ...state,
    isShowConsolidatedInvoiceLoading: false,
    consolidatedInvoice: payload,
  };
}

function onShowConsolidatedInvoiceError(state) {
  return {
    ...state,
    isShowConsolidatedInvoiceLoading: false,
  };
}

function onClearConsolidatedInvoiceModal(state) {
  return {
    ...state,
    consolidatedInvoice: [],
  };
}

export { validate };
export default createReducer(initState, {
  [actions.LOAD]: onLoad,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.EDIT]: onEdit,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.CANCEL]: onCancel,
  [actions.SAVE]: onSave,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.TOUCH]: onTouch,
  [actions.DELETE]: onDeleteRequest,
  [actions.DELETE_SUCCESS]: onDeleteSuccess,
  [actions.GET_LOCATION]: onGetLocation,
  [actions.RECEIVE_PAUSE_REASONS]: onPauseReasons,
  [actions.CHANGE_REASONS]: onchangeReason,
  [actions.CHANGE_REASONS_COMMENT]: onChangeReasonComment,
  [actions.UPDATE_PAUSE_MA_ACTION]: updatePauseMaAction,
  [actions.UPDATE_RESUME_MA_ACTION]: updateResumeMaAction,
  [actions.SHOW_MODAL]: onShowModal,
  [actions.SHOW_JOB_MODAL]: onShowJobModal,
  [actions.TOGGLE_ADD_GIG_MODAL]: onShowAddGigModal,
  [actions.CLOSE_MODAL]: onCloseModal,
  [actions.RESUME_MA_SUCCESS]: resumeMaSuccess,
  [actions.ON_PAUSE_MA]: onMaCall,
  [actions.TOGGLE_ADD_OTHER_PAYMENTS_MODAL]: onShowAddOtherPaymentsModal,
  [actions.TOGGLE_SHOW_MORE]: onToggleShowMore,
  [actions.LOAD_SHIFTS_SUCCESS]: onLoadShiftSuccess,
  [actions.LOAD_CANCELLED_SHIFTS_SUCCESS]: onLoadCancelledShiftSuccess,
  [actions.LOAD_BACKUP_SHIFTS_SUCCESS]: onLoadBackupShiftSuccess,
  [actions.LOAD_REPORTED_SHIFTS_SUCCESS]: onLoadReportedShiftSuccess,
  [actions.SHOW_ACCREDITATION_SUMMARY_MODAL]: showAccreditationSummaryModal,
  [actions.HIDE_ACCREDITATION_SUMMARY_MODAL]: hideAccreditationSummaryModal,
  [actions.REOPEN_POOLS_REQUEST]: reopenPools,
  [actions.REOPEN_POOLS_SUCCESS]: reopenPoolsSuccess,
  [actions.REOPEN_POOLS_ERROR]: reopenPoolsError,
  [actions.SHOW_CONSOLIDATED_INVOICE_REQUEST]: onShowConsolidatedInvoiceRequest,
  [actions.SHOW_CONSOLIDATED_INVOICE_SUCCESS]: onShowConsolidatedInvoiceSuccess,
  [actions.SHOW_CONSOLIDATED_INVOICE_ERROR]: onShowConsolidatedInvoiceError,
  [actions.CLEAR_CONSOLIDATED_INVOICE_MODAL]: onClearConsolidatedInvoiceModal,
  [actions.LOAD_APPROVED_AFTER_JOB_CLOSED_REQUEST]: onLoadApprovedAfterJobClosedRequest,
  [actions.LOAD_APPROVED_AFTER_JOB_CLOSED_SUCCESS]: onLoadApprovedAfterJobClosedSuccess,
  [actions.LOAD_WORKER_SUBMISSIONS_REQUEST]: onLoadWorkerSubmissionsRequest,
  [actions.LOAD_WORKER_SUBMISSIONS_SUCCESS]: onLoadWorkerSubmissionsSuccess,
});
